import React from 'react';
import { Box, useTheme } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList } from 'recharts';
import { Public, QrCode } from '@mui/icons-material';
import { colors } from '../../theme/theme';

interface AnalyticsChartProps {
    data: any[];
    isMobile: boolean;
    customColors: any;
    onColorClick: (key: string) => void;
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ data, isMobile, customColors, onColorClick }) => {
    const theme = useTheme();

    const renderLegendText = (value: string, entry: any) => {
        let color = entry.color;
        let key = '';

        // Safely access customColors with fallbacks
        const safeColors = {
            web_connected: customColors?.web_connected || '#42a5f5',
            web_anonymous: customColors?.web_anonymous || '#546e7a',
            flyer_connected: customColors?.flyer_connected || '#AA7D41',
            flyer_anonymous: customColors?.flyer_anonymous || '#6d4c41',
            facebook_connected: customColors?.facebook_connected || '#1877F2',
            facebook_anonymous: customColors?.facebook_anonymous || '#89b6f8'
        };

        if (value === 'Web (Connecté)') { color = safeColors.web_connected; key = 'web_connected'; }
        if (value === 'Web (Anonyme)') { color = safeColors.web_anonymous; key = 'web_anonymous'; }
        if (value === 'Flyer (Connecté)') { color = safeColors.flyer_connected; key = 'flyer_connected'; }
        if (value === 'Flyer (Anonyme)') { color = safeColors.flyer_anonymous; key = 'flyer_anonymous'; }
        if (value === 'Facebook (Connecté)') { color = safeColors.facebook_connected; key = 'facebook_connected'; }
        if (value === 'Facebook (Anonyme)') { color = safeColors.facebook_anonymous; key = 'facebook_anonymous'; }

        return (
            <span
                style={{ color, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                onClick={() => onColorClick(key)}
                title="Cliquez pour changer la couleur"
            >
                {value.includes('Web') ? <Public fontSize="small" /> : value.includes('Facebook') ? <Box component="span" sx={{ fontWeight: 'bold', fontSize: 16 }}>f</Box> : <QrCode fontSize="small" />}
                {value}
            </span>
        );
    };

    const getSafeColor = (key: string) => {
        // Defaults
        const defaults: any = {
            web_connected: '#42a5f5',
            web_anonymous: '#546e7a',
            flyer_connected: '#AA7D41',
            flyer_anonymous: '#6d4c41',
            facebook_connected: '#1877F2',
            facebook_anonymous: '#89b6f8'
        };
        return customColors?.[key] || defaults[key] || '#ccc';
    };

    if (isMobile) {
        // Calculate height based on number of items to ensure scrollability
        // 50px per item ensures good touch usage and readability
        const containerHeight = Math.max(500, data.length * 50);

        return (
            <Box sx={{ height: containerHeight, mt: 2, overflowX: 'hidden' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={colors.table.row} horizontal={true} vertical={false} />
                        <XAxis type="number" stroke={theme.palette.text.secondary} />
                        <YAxis
                            dataKey="date"
                            type="category"
                            stroke={theme.palette.text.secondary}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => value.slice(8) + '/' + value.slice(5, 7)}
                            width={50}
                        />
                        <Tooltip
                            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                            contentStyle={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                            }}
                            labelStyle={{ color: theme.palette.text.primary, fontWeight: 'bold', marginBottom: 8 }}
                            labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        />
                        <Legend
                            verticalAlign="top"
                            height={120}
                            formatter={renderLegendText}
                        />
                        <Bar stackId="a" dataKey="visits_logged_in" name="Web (Connecté)" fill={getSafeColor('web_connected')} barSize={20}>
                            <LabelList dataKey="visits_logged_in" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                        </Bar>
                        <Bar stackId="a" dataKey="visits_anonymous" name="Web (Anonyme)" fill={getSafeColor('web_anonymous')} barSize={20}>
                            <LabelList dataKey="visits_anonymous" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                        </Bar>
                        <Bar stackId="a" dataKey="flyer_scans_logged_in" name="Flyer (Connecté)" fill={getSafeColor('flyer_connected')} barSize={20}>
                            <LabelList dataKey="flyer_scans_logged_in" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                        </Bar>
                        <Bar stackId="a" dataKey="flyer_scans_anonymous" name="Flyer (Anonyme)" fill={getSafeColor('flyer_anonymous')} barSize={20}>
                            <LabelList dataKey="flyer_scans_anonymous" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                        </Bar>
                        <Bar stackId="a" dataKey="facebook_visits_logged_in" name="Facebook (Connecté)" fill={getSafeColor('facebook_connected')} barSize={20}>
                            <LabelList dataKey="facebook_visits_logged_in" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                        </Bar>
                        <Bar stackId="a" dataKey="facebook_visits_anonymous" name="Facebook (Anonyme)" fill={getSafeColor('facebook_anonymous')} barSize={20}>
                            <LabelList dataKey="facebook_visits_anonymous" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                            <LabelList
                                dataKey="total"
                                position="right"
                                fill={theme.palette.text.primary}
                                formatter={(val: number) => val > 0 ? val : ''}
                                style={{ fontWeight: 'bold', fontSize: 14 }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    // Desktop Layout
    return (
        <Box sx={{ height: 450, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    layout="horizontal"
                    margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.table.row} horizontal={true} vertical={false} />
                    <XAxis
                        dataKey="date"
                        stroke={theme.palette.text.secondary}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value.slice(8) + '/' + value.slice(5, 7)}
                    />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip
                        cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                        contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                        }}
                        labelStyle={{ color: theme.palette.text.primary, fontWeight: 'bold', marginBottom: 8 }}
                        labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    />
                    <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={renderLegendText}
                    />
                    <Bar stackId="a" dataKey="visits_logged_in" name="Web (Connecté)" fill={getSafeColor('web_connected')}>
                        <LabelList dataKey="visits_logged_in" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                    </Bar>
                    <Bar stackId="a" dataKey="visits_anonymous" name="Web (Anonyme)" fill={getSafeColor('web_anonymous')}>
                        <LabelList dataKey="visits_anonymous" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                    </Bar>
                    <Bar stackId="a" dataKey="flyer_scans_logged_in" name="Flyer (Connecté)" fill={getSafeColor('flyer_connected')}>
                        <LabelList dataKey="flyer_scans_logged_in" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                    </Bar>
                    <Bar stackId="a" dataKey="flyer_scans_anonymous" name="Flyer (Anonyme)" fill={getSafeColor('flyer_anonymous')}>
                        <LabelList dataKey="flyer_scans_anonymous" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                    </Bar>
                    <Bar stackId="a" dataKey="facebook_visits_logged_in" name="Facebook (Connecté)" fill={getSafeColor('facebook_connected')}>
                        <LabelList dataKey="facebook_visits_logged_in" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                    </Bar>
                    <Bar stackId="a" dataKey="facebook_visits_anonymous" name="Facebook (Anonyme)" fill={getSafeColor('facebook_anonymous')}>
                        <LabelList dataKey="facebook_visits_anonymous" position="center" fill="white" formatter={(val: number) => val > 0 ? val : ''} style={{ fontWeight: 'bold', fontSize: 12 }} />
                        <LabelList
                            dataKey="total"
                            position="top"
                            fill={theme.palette.text.primary}
                            formatter={(val: number) => val > 0 ? val : ''}
                            style={{ fontWeight: 'bold', fontSize: 14 }}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};
